import { cfetch, BadStatusError } from '@confluence/network';
import { expValEquals } from '@confluence/feature-experiments';

import {
	isValidSnippetMetadataResponse,
	type SnippetMetadataPayload,
	type SnippetMetadataResponse,
} from './types/conversationalAiClientTypes.ts';

const COMMON_HEADERS = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
	'X-Experience-Id': 'confluence-snippets-metadata',
	'X-Product': 'confluence',
} as const;

// Convo-AI path (current source of truth, served by conversational-ai-platform).
const CONVO_AI_URL = '/gateway/api/assist/api/confluence/v1/snippets/metadata';

// Cc-smarts path (new home, served by cc-smarts). Reached via the
// cc-smarts-ai-feature stargate route, which strips /gateway/api and
// prepends /api before forwarding to cc-smarts.
const CC_SMARTS_URL = '/gateway/api/ai/v2/ai-feature/snippets/metadata';

export class SnippetMetadataError extends Error {
	statusCode: number | undefined;

	constructor(message: string, statusCode?: number) {
		super(message);
		this.name = 'SnippetMetadataError';
		this.statusCode = statusCode;
	}
}

/**
 * Fetches AI-generated name and description suggestions for a snippet.
 *
 * Routing: the SPRT experiment `cc_smarts_snippet_metadata_migration`
 * (param `useCcSmarts`) determines whether the request goes to cc-smarts
 * (new home) or to conversational-ai-platform (current source of truth).
 * Wire shape is byte-for-byte identical between the two backends; only the
 * URL changes.
 *
 * Caller must pass a SnippetMetadataPayload (e.g. { adfContent: JSON.stringify(adf) })
 * and cloudId from session data for the request to succeed.
 */
export const fetchSnippetMetadata = async (
	snippetMetadataPayload: SnippetMetadataPayload,
	cloudId: string,
): Promise<SnippetMetadataResponse> => {
	const url = expValEquals('cc_smarts_snippet_metadata_migration', 'useCcSmarts', true)
		? CC_SMARTS_URL
		: CONVO_AI_URL;

	let response: Response;
	try {
		response = await cfetch(url, {
			method: 'POST',
			headers: {
				...COMMON_HEADERS,
				'X-Cloudid': cloudId,
			},
			body: JSON.stringify(snippetMetadataPayload),
		});
	} catch (err) {
		if (BadStatusError.isBadStatusError(err)) {
			throw new SnippetMetadataError(
				`Snippet metadata request failed: ${err.response.statusText}`,
				err.response.status,
			);
		}
		throw err;
	}

	const data: SnippetMetadataResponse = await response.json();
	if (!isValidSnippetMetadataResponse(data)) {
		throw new SnippetMetadataError('Snippet metadata request failed: Invalid response');
	}
	return data;
};
